/* eslint-disable */

/**
 * Activity can be fetched for various screens/contexts on the app. This scope
 * defines which renderers will execute and ensure the right authorization is triggered.
 */
export enum ActivityScope {
  ACTIVITY_SCOPE_DEFAULT_DO_NOT_USE = "ACTIVITY_SCOPE_DEFAULT_DO_NOT_USE",
  /**
   * MY_ACTIVITY - Generally to be used for activity centered around the caller customer. This is for things like
   * your main activity feed or your savings feed.
   */
  MY_ACTIVITY = "MY_ACTIVITY",
  /**
   * SPONSORED_ACCOUNT - This scope is for the activity screen used by parents to view their teen activity. This scope will
   * trigger the appropriate auth check as well as renderers that scrub data that shouldn't be sent
   * down to the client
   */
  SPONSORED_ACCOUNT = "SPONSORED_ACCOUNT",
  /** MY_ACTIVITY_WEB_V2 - Scope for the new web client. Once it is fully rolled out, MY_ACTIVITY_WEB will be deprecated */
  MY_ACTIVITY_WEB_V2 = "MY_ACTIVITY_WEB_V2",
  /** THREADS - Scope for the threads view (entity based feed separate from Activity that requires payment history data) */
  THREADS = "THREADS",
  /**
   * CASH_TIES_ADMIN_PROCESSING - Scope for profile stats correction, a simple tool that needs payment data
   * to calculate the total money received/sent between two customers.
   */
  CASH_TIES_ADMIN_PROCESSING = "CASH_TIES_ADMIN_PROCESSING",
  /**
   * SPENDING_INSIGHTS - Scope for spending insights. This will be for gathering transactions that have been previously aggregated and
   * showing customers the transactions that are a part of the given insight. We will be using renderers to mimick
   * some of the behavior of searches initially.
   */
  SPENDING_INSIGHTS = "SPENDING_INSIGHTS",
  /** COMPLIANCE - Scope for compliance */
  COMPLIANCE = "COMPLIANCE",
  /** ACTIVITY_SEARCH_INDEXER - For use by activity-search-indexer, other teams shouldn't use this scope. */
  ACTIVITY_SEARCH_INDEXER = "ACTIVITY_SEARCH_INDEXER",
  /** ACTIVITY_EXPORT - For use by the activity-export service, other teams shouldn't use this scope. */
  ACTIVITY_EXPORT = "ACTIVITY_EXPORT",
  /** SUPPORT_TRANSACTION - Scope for Support. */
  SUPPORT_TRANSACTION = "SUPPORT_TRANSACTION",
  /** NOOP - Scope for having no renderers run for debugging purposes so we can see the raw data. */
  NOOP = "NOOP",
  /** C4B_EARNINGS_TRACKER - Scope for the C4B Earnings Tracker. */
  C4B_EARNINGS_TRACKER = "C4B_EARNINGS_TRACKER",
  /** FRANKLIN_STATEMENT_POPULATOR - Scope for the Franklin Statement Populator. */
  FRANKLIN_STATEMENT_POPULATOR = "FRANKLIN_STATEMENT_POPULATOR",
  /** AFTERPAY_APPLET - Scope for afterpay applet transaction list. */
  AFTERPAY_APPLET = "AFTERPAY_APPLET",
  /** GROW_TOOLS - Scope for the Grow Over Time Feed which includes things like BTC rounds ups, auto invests, etc. */
  GROW_TOOLS = "GROW_TOOLS",
  /** GLOBAL_SEARCH - Scope for global search used by grantly */
  GLOBAL_SEARCH = "GLOBAL_SEARCH",
  /** P2P_POOLS - Scope for P2P Pools. */
  P2P_POOLS = "P2P_POOLS",
  /** WEB_RECEIPTS - For for web receipts. This does NOT include web receipts within cash.app. */
  WEB_RECEIPTS = "WEB_RECEIPTS",
  /** MONEYBOT - Scope for Moneybot. */
  MONEYBOT = "MONEYBOT",
  /** DISCOVER_GLOBAL_SEARCH - Cash-Discover's Global Search for Activity */
  DISCOVER_GLOBAL_SEARCH = "DISCOVER_GLOBAL_SEARCH",
  /** GPT_STORE - Scope for GPT Store. */
  GPT_STORE = "GPT_STORE",
  /** MONEYBOT_SPONSORED_ACCOUNT - Scope for Moneybot but with sponsored accounts. */
  MONEYBOT_SPONSORED_ACCOUNT = "MONEYBOT_SPONSORED_ACCOUNT",
  /** GREEN_BENEFITS - Scope for Green Benefits activity feed showing eco-friendly transaction rewards. */
  GREEN_BENEFITS = "GREEN_BENEFITS",
  /**
   * TRUST_HUB - Scope for Trust Hub alerts (NDL, security lock disabled, etc.). Provides metrics isolation
   * and avoids running irrelevant renderers (counterparty, p2p enhancer, reactions).
   */
  TRUST_HUB = "TRUST_HUB",
}

export enum ActivityTokenType {
  ACTIVITY_TOKEN_TYPE_DEFAULT_DO_NOT_USE = "ACTIVITY_TOKEN_TYPE_DEFAULT_DO_NOT_USE",
  CUSTOMER_TOKEN = "CUSTOMER_TOKEN",
  /** CUSTOMER_TOKEN_SAVINGS_ACCOUNT - Customer token. Used to request _all_ Savings activity across all of a customer's savings goals. */
  CUSTOMER_TOKEN_SAVINGS_ACCOUNT = "CUSTOMER_TOKEN_SAVINGS_ACCOUNT",
  CUSTOMER_TOKEN_PAYCHECKS = "CUSTOMER_TOKEN_PAYCHECKS",
  CUSTOMER_TOKEN_CASH_CARD_TRANSACTION = "CUSTOMER_TOKEN_CASH_CARD_TRANSACTION",
  /** CUSTOMER_TOKEN_BILLS - Raw customer token. Used to identity activity items that should show in Bill Pay. */
  CUSTOMER_TOKEN_BILLS = "CUSTOMER_TOKEN_BILLS",
  /** CUSTOMER_TOKEN_BILL_TOKEN - Composite token set by Billy. Used to create activity feeds for a single Bill in Bill Pay. */
  CUSTOMER_TOKEN_BILL_TOKEN = "CUSTOMER_TOKEN_BILL_TOKEN",
  /** CUSTOMER_TOKEN_RUNNING_BALANCE_CASH - Running Balance Tokens */
  CUSTOMER_TOKEN_RUNNING_BALANCE_CASH = "CUSTOMER_TOKEN_RUNNING_BALANCE_CASH",
  CUSTOMER_TOKEN_RUNNING_BALANCE_SAVINGS = "CUSTOMER_TOKEN_RUNNING_BALANCE_SAVINGS",
  /** CUSTOMER_TOKEN_ORDERS - Raw customer token used to identify activity items that should appear in the Orders feed. */
  CUSTOMER_TOKEN_ORDERS = "CUSTOMER_TOKEN_ORDERS",
  /** CUSTOMER_TOKEN_LEDGER_REFERENCE - Ledger Reference Records, used to join ActivityItems to LedgerItems */
  CUSTOMER_TOKEN_LEDGER_REFERENCE = "CUSTOMER_TOKEN_LEDGER_REFERENCE",
  /**
   * SAVINGS_FOLDER_TOKEN - A token identifying activity items associated with a specific savings "folder". A savings "folder" can be either an
   * explicit savings goal or a customer's "General Savings" bucket. A customer may have one or more savings goals but
   * only one "General Savings" bucket.
   * This token type will be used by clients to request activity associated only with that specific activity folder for
   * rendering items in secondary activity feeds.
   * This token type will only be used as a secondary activity token type (the primary token type for the item will
   * still be a customer token) when writing savings activity items.
   */
  SAVINGS_FOLDER_TOKEN = "SAVINGS_FOLDER_TOKEN",
  P2P_POOL_TOKEN = "P2P_POOL_TOKEN",
  /**
   * CUSTOMER_TOKEN_TRUST_HUB_PENDING_REVIEW - Secondary token isolating Trust Hub alerts that are pending customer review (NDL, security lock
   * disabled, etc.). A subset of CUSTOMER_TOKEN_TRUST_HUB, used to power the active alerts view.
   */
  CUSTOMER_TOKEN_TRUST_HUB_PENDING_REVIEW = "CUSTOMER_TOKEN_TRUST_HUB_PENDING_REVIEW",
  /**
   * CUSTOMER_TOKEN_TRUST_HUB - Secondary token for the Trust Hub feed containing all alerts regardless of review status.
   * The superset of CUSTOMER_TOKEN_TRUST_HUB_PENDING_REVIEW — used for viewing full alert history.
   */
  CUSTOMER_TOKEN_TRUST_HUB = "CUSTOMER_TOKEN_TRUST_HUB",
  /** CUSTOMER_TOKEN_EARNINGS - Earnings Home activity feed — includes paychecks and P2P earnings items. */
  CUSTOMER_TOKEN_EARNINGS = "CUSTOMER_TOKEN_EARNINGS",
  /**
   * CUSTOMER_TOKEN_EARNINGS_INCOME_STREAM - Earnings income stream feed — scoped to a specific income source.
   * Only to be used as a secondary activity token type.
   * sub_token identifies the income source (employer token for ACH, merchant token for OCT,
   * or a constant like "CASH_APP_SALES" for P2P earnings).
   */
  CUSTOMER_TOKEN_EARNINGS_INCOME_STREAM = "CUSTOMER_TOKEN_EARNINGS_INCOME_STREAM",
}

/**
 * Included in ActivityItemId and required for uniqueness constraints and for ACLs. A type should
 * represent the type of data being written and the recommendation is that only 1 service should be
 * responsible for writing 1 data type unless there is a migration going on.
 */
export enum ActivityItemType {
  DEFAULT_ACTIVITY_ITEM_TYPE_DO_NOT_USE = "DEFAULT_ACTIVITY_ITEM_TYPE_DO_NOT_USE",
  CASH_IN = "CASH_IN",
  CASH_OUT = "CASH_OUT",
  SAVINGS_INTERNAL_TRANSFER = "SAVINGS_INTERNAL_TRANSFER",
  P2P_FIAT_PAYMENT = "P2P_FIAT_PAYMENT",
  COUPON_ACTIVITY = "COUPON_ACTIVITY",
  REMITTANCE_PAYMENT = "REMITTANCE_PAYMENT",
  CASH_CARD_TRANSACTION = "CASH_CARD_TRANSACTION",
  P2P_BTC_PAYMENT = "P2P_BTC_PAYMENT",
  P2P_FRANKLIN_PAYMENT = "P2P_FRANKLIN_PAYMENT",
  CRYPTO_EXCHANGE = "CRYPTO_EXCHANGE",
  /** TTP_ACTIVITY - Cash Business Accounts Tap to Pay Activity */
  TTP_ACTIVITY = "TTP_ACTIVITY",
  /** PAYCHECKS - Activity feed within Paychecks Home */
  PAYCHECKS = "PAYCHECKS",
  SAVINGS_INTEREST_PAYMENT = "SAVINGS_INTEREST_PAYMENT",
  NEW_DEVICE_LOGIN = "NEW_DEVICE_LOGIN",
  P2P_INVEST_PAYMENT = "P2P_INVEST_PAYMENT",
  PENDING_REFERRAL = "PENDING_REFERRAL",
  /**
   * PAY_WITH_CASH_PAYMENT - DEPRECATED. Migrating to CASH_APP_PAY_PAYMENT.
   *
   * @deprecated
   */
  PAY_WITH_CASH_PAYMENT = "PAY_WITH_CASH_PAYMENT",
  /**
   * PAY_WITH_CASH_REFUND - DEPRECATED. Migrating to CASH_APP_PAY_REFUND.
   *
   * @deprecated
   */
  PAY_WITH_CASH_REFUND = "PAY_WITH_CASH_REFUND",
  SPONSORED_ACTIVITY = "SPONSORED_ACTIVITY",
  PAPER_MONEY_DEPOSIT = "PAPER_MONEY_DEPOSIT",
  LOYALTY_ACTIVITY = "LOYALTY_ACTIVITY",
  LIGHTNING_DEPOSIT_BTC = "LIGHTNING_DEPOSIT_BTC",
  LIGHTNING_WITHDRAWAL_BTC = "LIGHTNING_WITHDRAWAL_BTC",
  CHECK_DEPOSIT = "CHECK_DEPOSIT",
  GIFT_CARD_PAYMENT = "GIFT_CARD_PAYMENT",
  CRYPTO_PAYROLL_PREFERENCE = "CRYPTO_PAYROLL_PREFERENCE",
  WITHDRAWAL_BTC = "WITHDRAWAL_BTC",
  WIRE_TRANSFER = "WIRE_TRANSFER",
  DEPOSIT_BTC = "DEPOSIT_BTC",
  AFTERPAY_SUMMARY = "AFTERPAY_SUMMARY",
  C4B_ONBOARDING = "C4B_ONBOARDING",
  /** P2P_SHADOW_PAYMENT - This payment time is only to be used in staging for shadow tests */
  P2P_SHADOW_PAYMENT = "P2P_SHADOW_PAYMENT",
  RECURRING_BUY_BTC = "RECURRING_BUY_BTC",
  OVERDRAFT_TRANSACTION = "OVERDRAFT_TRANSACTION",
  LOAN_TRANSACTION = "LOAN_TRANSACTION",
  SPONSORSHIP_FEATURE_INVITATION = "SPONSORSHIP_FEATURE_INVITATION",
  INSTANT_PAY_SETTLEMENT = "INSTANT_PAY_SETTLEMENT",
  /** BANKING_TRANSACTION - All transactionwriter types default to this type. */
  BANKING_TRANSACTION = "BANKING_TRANSACTION",
  /** BILL_PAYMENT_PAYMENTUS - Bill payment sent through Paymentus, a vendor used for bill pay. */
  BILL_PAYMENT_PAYMENTUS = "BILL_PAYMENT_PAYMENTUS",
  /** CASH_APP_PAY_DEPOSITS - Cash App Pay Activity Items */
  CASH_APP_PAY_DEPOSITS = "CASH_APP_PAY_DEPOSITS",
  CASH_APP_PAY_PAYMENT = "CASH_APP_PAY_PAYMENT",
  CASH_APP_PAY_REFUND = "CASH_APP_PAY_REFUND",
  CASH_APP_ORDER = "CASH_APP_ORDER",
  CASH_APP_PAYOUT = "CASH_APP_PAYOUT",
  /** INVESTING_AUTOMATION_EXECUTION - Investing Types */
  INVESTING_AUTOMATION_EXECUTION = "INVESTING_AUTOMATION_EXECUTION",
  INVESTMENT_CORPORATE_ACTION = "INVESTMENT_CORPORATE_ACTION",
  INVESTMENT_CORRECTION = "INVESTMENT_CORRECTION",
  INVESTMENT_DIVIDEND = "INVESTMENT_DIVIDEND",
  INVESTMENT_ORDER = "INVESTMENT_ORDER",
  INVESTING_LEDGER = "INVESTING_LEDGER",
  /** LEDGER - LedgerEvent Items (for Running Balance) */
  LEDGER = "LEDGER",
  /**
   * LEDGER_REFERENCE - Reference Items to join LedgerItems to ActivityItems
   * When Ledgers are queried they look up the corresponding ActivityItem for display
   * Many Ledgers correlate 1:1 with ActivityItems, using either the transaction token or external id
   * Some Ledger Events cannot correlate using either value
   * For these LedgerItems the reference is looked up first, which points to the ActivityItem
   * Ledger References are written by CreateOrUpdateRequests that include ActivityItemCorrelations
   */
  LEDGER_REFERENCE = "LEDGER_REFERENCE",
  /**
   * LEDGER_ACTIVITY_REFERENCE - Reference Items to join ActivityItems to LedgerItems
   * When ActivityItems are written they need to query the Ledger item to sync SECTION
   * For LedgerItems that don't correlate by transaction token, ActivityItems need a way
   * To lookup the ledger item. This is done by writing a reference item that points
   * from the ActivityItem correlation value to the Ledger transaction token
   */
  LEDGER_ACTIVITY_REFERENCE = "LEDGER_ACTIVITY_REFERENCE",
  /**
   * LEDGER_EVENT_NO_ACTIVITY_ITEM - This activity item type will never be used to map to an actual activity item, but needed to
   * process the ledger event.
   * For example, on staging, cash app pay tests create transactions in Banklin to force a
   * customer's balance to a specific amount before the test run, so that the tests don't fail with
   * NSF.
   */
  LEDGER_EVENT_NO_ACTIVITY_ITEM = "LEDGER_EVENT_NO_ACTIVITY_ITEM",
  /** LOCAL_CASHBACK - Cash App Local Activity Items */
  LOCAL_CASHBACK = "LOCAL_CASHBACK",
  LOCAL_ORDER = "LOCAL_ORDER",
  /**
   * SAVINGS_CASH_IN - Savings External Cash In Activity Items
   * These items are written by piggybank for external cash ins (e.g. from a linked debit card) to a customer's
   * savings balance.
   */
  SAVINGS_CASH_IN = "SAVINGS_CASH_IN",
  /**
   * BANKING_TRANSACTION_V2 - Activity items coming from transactionwriter. V2 represents transaction events from Banklin using LedgerEvent
   * as opposed to the legacy TransactionEvent
   */
  BANKING_TRANSACTION_V2 = "BANKING_TRANSACTION_V2",
  /** SPONSOR_PAYMENT_APPROVAL - Activity items coming from aegis that is sent to a sponsor showing a payment involving their dependent that needs/needed their approval. */
  SPONSOR_PAYMENT_APPROVAL = "SPONSOR_PAYMENT_APPROVAL",
  /** STABLE_WITHDRAWAL_USDC - Activity items coming from block-stable that are sent to a customer showing their stablecoin deposits and withdrawals. */
  STABLE_WITHDRAWAL_USDC = "STABLE_WITHDRAWAL_USDC",
  STABLE_DEPOSIT_USDC = "STABLE_DEPOSIT_USDC",
  P2P_POOL = "P2P_POOL",
  /** C4B_LISTING_PAYMENT_ACTIVITY - Cash for Business Listing Payment Activity */
  C4B_LISTING_PAYMENT_ACTIVITY = "C4B_LISTING_PAYMENT_ACTIVITY",
  CUSTOMER_ADJUSTMENT = "CUSTOMER_ADJUSTMENT",
  /**
   * SAVINGS_SCHEDULED_AUTO_TRANSFER - Activity items representing upcoming scheduled auto-transfers to savings goals or general savings.
   * These are non-balance-impacting items showing pending/future auto-transfers that haven't yet executed.
   * Written by piggybank after processing a scheduled transfer (to create the next upcoming item) and
   * by invest-automator after schedule setup (to create initial upcoming items).
   * Appears in goal home (for goal-specific transfers via SAVINGS_FOLDER_TOKEN) or general savings home
   * (for general savings transfers via CUSTOMER_TOKEN_SAVINGS_ACCOUNT only).
   * Shows the next 2 upcoming scheduled auto-transfers. When a transfer executes, the pending item is
   * updated/replaced with the actual transfer receipt, and a new pending item is created for the next-next transfer.
   */
  SAVINGS_SCHEDULED_AUTO_TRANSFER = "SAVINGS_SCHEDULED_AUTO_TRANSFER",
  /** LIFTED_RESTRICTION_TRANSFER - Money movement from the restricted balance to the primary balances */
  LIFTED_RESTRICTION_TRANSFER = "LIFTED_RESTRICTION_TRANSFER",
  /** CASH_LITE_ONRAMP_CASH_IN - Activity items coming from cashlite-flow representing a cash-in from an onramp (e.g. buying stablecoins). */
  CASH_LITE_ONRAMP_CASH_IN = "CASH_LITE_ONRAMP_CASH_IN",
}

/**
 * This field will be a list of the relevant product classifiers that identify the product. This is not equivalent to
 * ActivityItemType although it may align in some cases. None of these classifiers have to be comprehensive of a product
 * as they are going to
 */
export enum ActivityProductClassifier {
  APC_DEFAULT_DO_NOT_USE = "APC_DEFAULT_DO_NOT_USE",
  /** APC_ACH_TRANSFER - Borrow lending product. */
  APC_ACH_TRANSFER = "APC_ACH_TRANSFER",
  APC_ACH_TRANSFER_RETURN = "APC_ACH_TRANSFER_RETURN",
  APC_AFTERPAY_SUMMARY = "APC_AFTERPAY_SUMMARY",
  APC_ALLOWANCE = "APC_ALLOWANCE",
  APC_ATM = "APC_ATM",
  APC_AUTOMATED = "APC_AUTOMATED",
  APC_BALANCE_IMPACT_CASH = "APC_BALANCE_IMPACT_CASH",
  APC_BALANCE_IMPACT_SAVINGS = "APC_BALANCE_IMPACT_SAVINGS",
  APC_BIDV_FAILED = "APC_BIDV_FAILED",
  APC_BIDV_IN_REVIEW = "APC_BIDV_IN_REVIEW",
  APC_BIDV_PASSED = "APC_BIDV_PASSED",
  APC_BILL_PAYMENT = "APC_BILL_PAYMENT",
  APC_BITKEY_TRANSFER = "APC_BITKEY_TRANSFER",
  /** APC_BORROW - Borrow lending product. */
  APC_BORROW = "APC_BORROW",
  APC_BTC = "APC_BTC",
  APC_BTC_AUTO_WITHDRAWAL = "APC_BTC_AUTO_WITHDRAWAL",
  APC_BTC_BOOST = "APC_BTC_BOOST",
  APC_BTC_BUY = "APC_BTC_BUY",
  APC_BTC_LIGHTNING_NETWORK = "APC_BTC_LIGHTNING_NETWORK",
  APC_BTC_SELL = "APC_BTC_SELL",
  APC_BUSINESS_ACCOUNT_ONBOARDING = "APC_BUSINESS_ACCOUNT_ONBOARDING",
  APC_BUSINESS_CARD = "APC_BUSINESS_CARD",
  /** APC_C4B_LISTING_PAYMENT - Cash for Business Listing Payment */
  APC_C4B_LISTING_PAYMENT = "APC_C4B_LISTING_PAYMENT",
  APC_CASH_APP_LOCAL = "APC_CASH_APP_LOCAL",
  APC_CASH_APP_LOCAL_CASHBACK = "APC_CASH_APP_LOCAL_CASHBACK",
  APC_CASH_APP_LOCAL_ORDER = "APC_CASH_APP_LOCAL_ORDER",
  APC_CASH_APP_ORDER = "APC_CASH_APP_ORDER",
  APC_CASH_APP_PAY = "APC_CASH_APP_PAY",
  APC_CASH_APP_PAY_DEPOSIT = "APC_CASH_APP_PAY_DEPOSIT",
  APC_CASH_APP_PAY_LATER = "APC_CASH_APP_PAY_LATER",
  APC_CASH_APP_PAY_NOW = "APC_CASH_APP_PAY_NOW",
  APC_CASH_APP_PAY_PAYMENT = "APC_CASH_APP_PAY_PAYMENT",
  APC_CASH_APP_PAY_REFUND = "APC_CASH_APP_PAY_REFUND",
  APC_CASH_APP_PAYOUT = "APC_CASH_APP_PAYOUT",
  APC_CASH_CARD = "APC_CASH_CARD",
  APC_CASH_CARD_ACCOUNT_FUNDING_TRANSACTION = "APC_CASH_CARD_ACCOUNT_FUNDING_TRANSACTION",
  APC_CASH_CARD_CASHBACK = "APC_CASH_CARD_CASHBACK",
  APC_CASH_CARD_ORIGINAL_CREDIT_TRANSACTION = "APC_CASH_CARD_ORIGINAL_CREDIT_TRANSACTION",
  APC_CASH_CARD_QUASI_CASH = "APC_CASH_CARD_QUASI_CASH",
  APC_CASH_CARD_RETURN = "APC_CASH_CARD_RETURN",
  /** APC_CASH_IN - Represents any product where cash is moved into Cash App. */
  APC_CASH_IN = "APC_CASH_IN",
  /** APC_CASH_OUT - Represents any product where cash is moved out of Cash App. */
  APC_CASH_OUT = "APC_CASH_OUT",
  APC_CHECK_DEPOSIT = "APC_CHECK_DEPOSIT",
  APC_COUPON = "APC_COUPON",
  APC_CRYPTO_EXCHANGE = "APC_CRYPTO_EXCHANGE",
  APC_CRYPTO_TRADING_SETTLEMENT = "APC_CRYPTO_TRADING_SETTLEMENT",
  APC_CUSTOMER_ADJUSTMENT = "APC_CUSTOMER_ADJUSTMENT",
  /** APC_CUSTOMER_ADJUSTMENT_CLAWBACK - Customer adjustment classifier for clawback. */
  APC_CUSTOMER_ADJUSTMENT_CLAWBACK = "APC_CUSTOMER_ADJUSTMENT_CLAWBACK",
  /** APC_CUSTOMER_ADJUSTMENT_REIMBURSEMENT - Customer adjustment classifier for reimbursement. */
  APC_CUSTOMER_ADJUSTMENT_REIMBURSEMENT = "APC_CUSTOMER_ADJUSTMENT_REIMBURSEMENT",
  APC_DEBIT_TRANSFER = "APC_DEBIT_TRANSFER",
  APC_DEPOSIT_REVERSAL = "APC_DEPOSIT_REVERSAL",
  APC_DEVICE_LOGIN = "APC_DEVICE_LOGIN",
  APC_ESTIMATED_INTEREST_PAYOUT = "APC_ESTIMATED_INTEREST_PAYOUT",
  APC_EXPIRED_COUPON = "APC_EXPIRED_COUPON",
  APC_EXTERNAL_TRANSFER = "APC_EXTERNAL_TRANSFER",
  APC_FEE = "APC_FEE",
  APC_FIAT = "APC_FIAT",
  /** APC_GENERIC_BTC_TRANSACTION - Generic BTC transaction for sending BTC from one transaction to another. */
  APC_GENERIC_BTC_TRANSACTION = "APC_GENERIC_BTC_TRANSACTION",
  APC_GIFT_CARD = "APC_GIFT_CARD",
  APC_GIVEAWAY = "APC_GIVEAWAY",
  APC_INSTANT_PAY = "APC_INSTANT_PAY",
  APC_INSTANT_TRANSFER = "APC_INSTANT_TRANSFER",
  APC_INTEREST_PAYOUT = "APC_INTEREST_PAYOUT",
  APC_INTERNAL_TRANSFER = "APC_INTERNAL_TRANSFER",
  APC_INVEST = "APC_INVEST",
  APC_INVEST_CORPORATE_ACTION = "APC_INVEST_CORPORATE_ACTION",
  APC_INVEST_CORRECTION = "APC_INVEST_CORRECTION",
  APC_INVEST_DIVIDEND = "APC_INVEST_DIVIDEND",
  APC_INVEST_ORDER = "APC_INVEST_ORDER",
  APC_ISSUER_PROCESSING = "APC_ISSUER_PROCESSING",
  APC_KYB_COMPLETED = "APC_KYB_COMPLETED",
  APC_LENDING = "APC_LENDING",
  /** APC_LENDING_PRE_PURCHASE_FINANCING - Cash App Card pre-purchase financing product. */
  APC_LENDING_PRE_PURCHASE_FINANCING = "APC_LENDING_PRE_PURCHASE_FINANCING",
  /** APC_LENDING_RETROACTIVE_FINANCING - Retroactive financing product. */
  APC_LENDING_RETROACTIVE_FINANCING = "APC_LENDING_RETROACTIVE_FINANCING",
  APC_LENDING_RETROACTIVE_FINANCING_ELIGIBLE = "APC_LENDING_RETROACTIVE_FINANCING_ELIGIBLE",
  /** APC_LENDING_SINGLE_USE_PAYMENT - Single-use payments product. */
  APC_LENDING_SINGLE_USE_PAYMENT = "APC_LENDING_SINGLE_USE_PAYMENT",
  APC_LIFTED_RESTRICTION_TRANSFER = "APC_LIFTED_RESTRICTION_TRANSFER",
  APC_LIGHTNING_DEPOSIT = "APC_LIGHTNING_DEPOSIT",
  APC_LIGHTNING_WITHDRAWAL = "APC_LIGHTNING_WITHDRAWAL",
  APC_LOYALTY = "APC_LOYALTY",
  APC_MARQETA = "APC_MARQETA",
  APC_NON_BALANCE_IMPACTING_ITEM = "APC_NON_BALANCE_IMPACTING_ITEM",
  APC_OVERDRAFT = "APC_OVERDRAFT",
  APC_P2P = "APC_P2P",
  APC_P2P_EXTERNALLY_FUNDED = "APC_P2P_EXTERNALLY_FUNDED",
  /** APC_P2P_OON - Out of network peer to peer payment. */
  APC_P2P_OON = "APC_P2P_OON",
  /** APC_P2P_PROSPECT - P2P payment sent to a non-Cash App user (prospect). */
  APC_P2P_PROSPECT = "APC_P2P_PROSPECT",
  /** APC_P2P_RECEIVE_AS_BITCOIN - P2P payment received as bitcoin. */
  APC_P2P_RECEIVE_AS_BITCOIN = "APC_P2P_RECEIVE_AS_BITCOIN",
  APC_P2P_POOL = "APC_P2P_POOL",
  APC_P2P_POOL_CLOSURE = "APC_P2P_POOL_CLOSURE",
  APC_P2P_POOL_CONTRIBUTION = "APC_P2P_POOL_CONTRIBUTION",
  APC_P2P_POOL_GOAL_REACHED = "APC_P2P_POOL_GOAL_REACHED",
  APC_P2P_POOL_INVITE = "APC_P2P_POOL_INVITE",
  /** APC_P2P_POOL_STATUS - An item that indicates the status of the pool to the owner of said pool. */
  APC_P2P_POOL_STATUS = "APC_P2P_POOL_STATUS",
  APC_PAPER_MONEY_DEPOSIT = "APC_PAPER_MONEY_DEPOSIT",
  APC_PAYCHECK = "APC_PAYCHECK",
  APC_PAYCHECK_DISTRIBUTION = "APC_PAYCHECK_DISTRIBUTION",
  APC_RECURRING = "APC_RECURRING",
  APC_REDEEMED_COUPON = "APC_REDEEMED_COUPON",
  APC_REFERRAL = "APC_REFERRAL",
  APC_REFUND = "APC_REFUND",
  APC_REMITTANCE = "APC_REMITTANCE",
  APC_ROUND_UP = "APC_ROUND_UP",
  APC_RUNNING_BALANCE_LINK = "APC_RUNNING_BALANCE_LINK",
  APC_SAVINGS = "APC_SAVINGS",
  APC_SHAZAM = "APC_SHAZAM",
  APC_SLOW_TRANSFER = "APC_SLOW_TRANSFER",
  APC_SPONSORED_ACTIVITY = "APC_SPONSORED_ACTIVITY",
  APC_SPONSORSHIP = "APC_SPONSORSHIP",
  APC_SPONSORSHIP_FEATURE_INVITATION = "APC_SPONSORSHIP_FEATURE_INVITATION",
  APC_SPONSOR_PAYMENT_APPROVAL = "APC_SPONSOR_PAYMENT_APPROVAL",
  APC_SQUARE_BTC_SAVINGS = "APC_SQUARE_BTC_SAVINGS",
  APC_SQUARE_PAYROLL = "APC_SQUARE_PAYROLL",
  APC_SQUARE_PAYROLL_TIPS = "APC_SQUARE_PAYROLL_TIPS",
  APC_STABLE = "APC_STABLE",
  APC_STABLE_DEPOSIT = "APC_STABLE_DEPOSIT",
  APC_STABLE_WITHDRAWAL = "APC_STABLE_WITHDRAWAL",
  APC_STOCK_BUY = "APC_STOCK_BUY",
  APC_STOCK_SELL = "APC_STOCK_SELL",
  APC_TAP_TO_PAY = "APC_TAP_TO_PAY",
  /** APC_TIDAL_CONTRIBUTION - Tidal Listener to Artist P2P payment */
  APC_TIDAL_CONTRIBUTION = "APC_TIDAL_CONTRIBUTION",
  APC_VISA_DPS = "APC_VISA_DPS",
  /** APC_WIRE_TRANSFER - tags are out of order next is 126; */
  APC_WIRE_TRANSFER = "APC_WIRE_TRANSFER",
}

export enum ActivityDisplayCategory {
  ADC_DEFAULT_DO_NOT_USE = "ADC_DEFAULT_DO_NOT_USE",
  ADC_BANKING = "ADC_BANKING",
  ADC_BITCOIN = "ADC_BITCOIN",
  ADC_CASH_APP_LOCAL = "ADC_CASH_APP_LOCAL",
  ADC_CASH_APP_PAY = "ADC_CASH_APP_PAY",
  ADC_CASH_CARD = "ADC_CASH_CARD",
  ADC_CASH_FOR_BUSINESS = "ADC_CASH_FOR_BUSINESS",
  ADC_INVESTING = "ADC_INVESTING",
  ADC_LENDING = "ADC_LENDING",
  ADC_P2P = "ADC_P2P",
  ADC_PAYCHECKS = "ADC_PAYCHECKS",
  ADC_REFERRALS = "ADC_REFERRALS",
  ADC_SAVINGS = "ADC_SAVINGS",
  ADC_STABLE = "ADC_STABLE",
  ADC_TRANSFERS = "ADC_TRANSFERS",
  ADC_USER_ACTIVITY = "ADC_USER_ACTIVITY",
}

export enum ActivityDisplayType {
  ADT_DEFAULT_DO_NOT_USE = "ADT_DEFAULT_DO_NOT_USE",
  ADT_ACCOUNT_NOTIFICATIONS = "ADT_ACCOUNT_NOTIFICATIONS",
  ADT_ATM_WITHDRAWAL = "ADT_ATM_WITHDRAWAL",
  ADT_AUTO_INVEST = "ADT_AUTO_INVEST",
  ADT_BANK_TRANSFER = "ADT_BANK_TRANSFER",
  ADT_BILL_PAY = "ADT_BILL_PAY",
  ADT_BITCOIN_BUY = "ADT_BITCOIN_BUY",
  ADT_BITCOIN_DEPOSIT = "ADT_BITCOIN_DEPOSIT",
  ADT_BITCOIN_PAYMENT = "ADT_BITCOIN_PAYMENT",
  ADT_BITCOIN_RECURRING_BUY = "ADT_BITCOIN_RECURRING_BUY",
  ADT_BITCOIN_ROUND_UP_BUY = "ADT_BITCOIN_ROUND_UP_BUY",
  ADT_BITCOIN_SELL = "ADT_BITCOIN_SELL",
  ADT_BITCOIN_WITHDRAWAL = "ADT_BITCOIN_WITHDRAWAL",
  ADT_BORROW = "ADT_BORROW",
  ADT_CASH_APP_LOCAL = "ADT_CASH_APP_LOCAL",
  ADT_CASH_APP_PAY_DEPOSIT = "ADT_CASH_APP_PAY_DEPOSIT",
  ADT_CASH_APP_PAY_PAYMENT = "ADT_CASH_APP_PAY_PAYMENT",
  ADT_CASH_APP_PAY_REFUND = "ADT_CASH_APP_PAY_REFUND",
  ADT_CASH_APP_PAYOUT = "ADT_CASH_APP_PAYOUT",
  ADT_CASH_CARD = "ADT_CASH_CARD",
  ADT_CHECK_DEPOSIT = "ADT_CHECK_DEPOSIT",
  ADT_CUSTOMER_ADJUSTMENT = "ADT_CUSTOMER_ADJUSTMENT",
  ADT_DEPOSITS = "ADT_DEPOSITS",
  ADT_FAMILY = "ADT_FAMILY",
  ADT_GIFT_CARD_P2P = "ADT_GIFT_CARD_P2P",
  ADT_INSTANT_PAY = "ADT_INSTANT_PAY",
  ADT_INVESTING = "ADT_INVESTING",
  ADT_LIGHTNING_DEPOSIT = "ADT_LIGHTNING_DEPOSIT",
  ADT_LIGHTNING_WITHDRAWAL = "ADT_LIGHTNING_WITHDRAWAL",
  ADT_LOYALTY_REWARDS = "ADT_LOYALTY_REWARDS",
  ADT_OTHER = "ADT_OTHER",
  ADT_OVERDRAFT = "ADT_OVERDRAFT",
  ADT_P2P = "ADT_P2P",
  ADT_P2P_POOL = "ADT_P2P_POOL",
  ADT_PAPER_MONEY_DEPOSIT = "ADT_PAPER_MONEY_DEPOSIT",
  ADT_PAYCHECKS = "ADT_PAYCHECKS",
  ADT_REFERRALS = "ADT_REFERRALS",
  ADT_REMITTANCES = "ADT_REMITTANCES",
  ADT_SAVINGS_INTEREST_PAYMENT = "ADT_SAVINGS_INTEREST_PAYMENT",
  ADT_SAVINGS_INTERNAL_TRANSFER = "ADT_SAVINGS_INTERNAL_TRANSFER",
  ADT_SAVINGS_ROUND_UP = "ADT_SAVINGS_ROUND_UP",
  ADT_SAVINGS_SCHEDULED_TRANSFER = "ADT_SAVINGS_SCHEDULED_TRANSFER",
  ADT_STABLE_DEPOSIT = "ADT_STABLE_DEPOSIT",
  ADT_STABLE_WITHDRAWAL = "ADT_STABLE_WITHDRAWAL",
  ADT_STOCK_BUY = "ADT_STOCK_BUY",
  ADT_STOCK_DIVIDENDS = "ADT_STOCK_DIVIDENDS",
  ADT_STOCK_P2P = "ADT_STOCK_P2P",
  ADT_STOCK_ROUND_UP_BUY = "ADT_STOCK_ROUND_UP_BUY",
  ADT_STOCK_SELL = "ADT_STOCK_SELL",
  ADT_TAP_TO_PAY_PAYMENTS = "ADT_TAP_TO_PAY_PAYMENTS",
  ADT_WIRE_TRANSFER = "ADT_WIRE_TRANSFER",
  ADT_WITHDRAWAL = "ADT_WITHDRAWAL",
}

export interface ActivityToken {
  /**
   * This is the token that indicates which activity stream we want to get (e.g. for a customer, or
   * group..etc)
   *
   * The activity_token_type field is required and is used to understand what the actual meaning of the token
   * and sub_token is. For example, CUSTOMER_TOKEN type indicates that token should be the customer_token, and
   * sub_token should be null
   */
  activity_token_type?: ActivityTokenType;
  /**
   * token is required and should represent the ownership or a token that we are able to verify ownership through.
   * This doesn't mean it has to be a customer token. For example, it could be some kind of a group token
   * that lets us discern via authorization checks whether a particular person has access to it.
   */
  token?: string;
  /**
   * sub_token is optional. It is there to allow as needed a composite activity token based on ownership (1st field)
   * and anything else (sub_token field). However, what the sub_token field means and whether it is present should
   * be consistent based on activity_token_type
   */
  sub_token?: string;
}

/**
 * An encoded value that is used to determine where to start for the next page of data. This is being deprecated in
 * favor of ActivityCursor.
 */
export interface ActivityOffset {
  known_cursors?: string[];
}

/**
 * An activity item represents a single item written to the activity service. An activity row represents
 * all the information required to render a single row. This includes the activity item + sender/recipient
 * information when applicable, badging, reactions..etc
 *
 * An activity item that represents a balance-impacting event should use an `id` that is either the
 * Banklin Transaction Token or the `external_system_id` of the corresponding Ledger Event to allow
 * Activity to correlate the two items.
 */
export interface ActivityItemId {
  /** Required. This id should be unique per actor and type. */
  id?: string;
  /**
   * Required. An activity is uniquely identifiable by its type, id, and actor token.
   * Services are required to have the right authorization in place to write a certain activity_type
   */
  type?: ActivityItemType;
}
